// Per-Talent state hoisted above the row component so it survives the
// section swap that happens when a toggle moves a row between the
// in_club / not_in_club groups. The row component subscribes via context
// and looks up its own state by talent_id.
//
// Two maps live here:
//   - `actors`: one xstate actor per talent_id, lazily created.
//   - `toastHandles`: one NotificationHandle slot per talent_id, written
//     by handle_tap, read by handle_undo (possibly from a different row
//     instance after the section swap).
//
// Both maps are stored in refs (no re-render on mutation). The context
// value itself is memoized so consumers don't churn on every page render.

import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useMemo,
	useRef,
} from 'react';
import { type ActorRefFrom, createActor } from 'xstate';

import type { NotificationHandle } from '~/_shared/design-system/ui/molecules/toast/notification-provider';

import { talentRowMachine } from './talent-row-machine';

export type TalentRowActor = ActorRefFrom<typeof talentRowMachine>;

interface TalentRowStoreContextValue {
	getActor(talentId: string): TalentRowActor;
	toastHandles: Map<string, NotificationHandle | null>;
}

const TalentRowStoreContext = createContext<TalentRowStoreContextValue | null>(
	null,
);

export const TalentRowStoreProvider = ({
	children,
}: {
	children: ReactNode;
}) => {
	const actorsRef = useRef<Map<string, TalentRowActor>>(new Map());
	const toastHandlesRef = useRef<Map<string, NotificationHandle | null>>(
		new Map(),
	);

	const getActor = useCallback((talentId: string): TalentRowActor => {
		const existing = actorsRef.current.get(talentId);
		if (existing) return existing;
		const actor = createActor(talentRowMachine, {
			input: { talent_id: talentId },
		}) as TalentRowActor;
		actor.start();
		actorsRef.current.set(talentId, actor);
		return actor;
	}, []);

	const value = useMemo<TalentRowStoreContextValue>(
		() => ({
			getActor,
			toastHandles: toastHandlesRef.current,
		}),
		[getActor],
	);

	return (
		<TalentRowStoreContext.Provider value={value}>
			{children}
		</TalentRowStoreContext.Provider>
	);
};

export function useTalentRowStore(): TalentRowStoreContextValue {
	const ctx = useContext(TalentRowStoreContext);
	/* v8 ignore next 5 — defensive: TalentRowItem always mounts inside the
	   Provider via LiveInClubPage's wrap. */
	if (!ctx) {
		throw new Error(
			'useTalentRowStore must be used inside a <TalentRowStoreProvider>',
		);
	}
	return ctx;
}
